import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Eye, Users, Download, TrendingUp, Smartphone, Globe, Clock, MonitorSmartphone } from "lucide-react";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
        <Icon className={`h-4 w-4 ${accent}`} /> {label}
      </div>
      <div className="mt-2 font-display text-3xl font-black text-zinc-50">{value}</div>
    </div>
  );
}

function BreakdownList({ title, icon: Icon, items, accent, testid, emptyText }) {
  const total = items.reduce((a, b) => a + b.count, 0) || 1;
  return (
    <div className="border border-zinc-800 bg-zinc-900 p-5" data-testid={testid}>
      <h3 className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">
        <Icon className={`h-4 w-4 ${accent}`} /> {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-zinc-500 text-sm">{emptyText || "Nessun dato ancora."}</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <div key={it.name}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-300">{it.name}</span>
                <span className="font-display font-bold text-zinc-100">{it.count}</span>
              </div>
              <div className="mt-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-400" style={{ width: `${Math.round((it.count / total) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "ora";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return `${Math.floor(s / 86400)} g fa`;
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/analytics/recent").then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  if (error) return <p className="text-red-400 py-10 text-center" data-testid="analytics-error">Impossibile caricare le statistiche.</p>;
  if (!data) return <p className="text-zinc-500 py-10 text-center" data-testid="analytics-loading">Caricamento statistiche…</p>;

  const avg = Math.round(data.daily.reduce((a, b) => a + b.visits, 0) / (data.daily.length || 1));

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Visite totali" value={data.total_visits} accent="text-yellow-400" testid="stat-total-visits" />
        <StatCard icon={Users} label="Visitatori unici" value={data.unique_visitors} accent="text-cyan-400" testid="stat-unique-visitors" />
        <StatCard icon={Download} label="Installazioni" value={data.installs} accent="text-green-400" testid="stat-installs" />
        <StatCard icon={TrendingUp} label="Visite oggi" value={data.visits_today} accent="text-fuchsia-400" testid="stat-visits-today" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-zinc-800 bg-zinc-900 p-4"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Ultimi 7 giorni</div><div className="mt-1 font-display text-2xl text-zinc-50" data-testid="stat-7d">{data.visits_7d}</div></div>
        <div className="border border-zinc-800 bg-zinc-900 p-4"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Ultimi 30 giorni</div><div className="mt-1 font-display text-2xl text-zinc-50" data-testid="stat-30d">{data.visits_30d}</div></div>
        <div className="border border-zinc-800 bg-zinc-900 p-4"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Media/giorno (14gg)</div><div className="mt-1 font-display text-2xl text-zinc-50">{avg}</div></div>
      </div>

      <div className="border border-zinc-800 bg-zinc-900 p-5">
        <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">Visite giornaliere (14 giorni)</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="day" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", color: "#fafafa" }} />
              <Bar dataKey="visits" fill="#facc15" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-zinc-800 bg-zinc-900 p-5">
        <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">Installazioni app (14 giorni)</h3>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="day" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", color: "#fafafa" }} />
              <Bar dataKey="installs" fill="#22c55e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-zinc-800 bg-zinc-900 p-5">
        <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">Pagine più viste</h3>
        {data.top_paths.length === 0 ? (
          <p className="text-zinc-500 text-sm">Nessun dato ancora.</p>
        ) : (
          <div className="space-y-2">
            {data.top_paths.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm border-b border-zinc-800 pb-2">
                <span className="text-zinc-300 truncate">{p.path}</span>
                <span className="font-display font-bold text-yellow-400">{p.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <BreakdownList title="Dispositivi" icon={MonitorSmartphone} items={data.devices || []} accent="text-cyan-400" testid="devices-list" />
        <BreakdownList title="Browser" icon={Smartphone} items={data.browsers || []} accent="text-fuchsia-400" testid="browsers-list" />
        <BreakdownList title="Paesi" icon={Globe} items={data.countries || []} accent="text-green-400" testid="countries-list" emptyText="Posizione non ancora disponibile" />
      </div>

      <div className="border border-zinc-800 bg-zinc-900 p-5" data-testid="recent-visits">
        <h3 className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">
          <Clock className="h-4 w-4" /> Visite recenti (anonime)
        </h3>
        {recent.length === 0 ? (
          <p className="text-zinc-500 text-sm">Nessuna visita registrata ancora.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 pr-3">Quando</th>
                  <th className="py-2 pr-3">Pagina</th>
                  <th className="py-2 pr-3">Dispositivo</th>
                  <th className="py-2">Posizione</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r, i) => (
                  <tr key={i} data-testid={`recent-visit-${i}`} className="border-b border-zinc-800/60">
                    <td className="py-2 pr-3 text-zinc-400 whitespace-nowrap">{timeAgo(r.ts)}</td>
                    <td className="py-2 pr-3 text-zinc-200 truncate max-w-[160px]">{r.path}</td>
                    <td className="py-2 pr-3 text-zinc-400 whitespace-nowrap">{r.device || "—"} · {r.browser || "—"}</td>
                    <td className="py-2 text-zinc-400 whitespace-nowrap">{r.city ? `${r.city}, ` : ""}{r.country || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[11px] text-zinc-500">
        Nota: i dati partono dal momento dell'attivazione. Le installazioni PWA sono una stima: su alcuni dispositivi (es. iPhone/Safari) l'installazione potrebbe non essere rilevabile dal browser. La posizione è approssimativa (stimata dall'IP) e può essere imprecisa con VPN o rete mobile.
      </p>
    </div>
  );
}
